import React, {memo, useCallback, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useController} from 'react-hook-form'
import {defineMessages, useIntl} from 'react-intl'
import {Box, Button, Menu, MenuButton, MenuItem, MenuList} from '@chakra-ui/react'
import Field from '../field'
import {FlagImage} from '../icons'
import {useCountry} from '../../hooks/use-country'
import {useSortedPhoneCountries} from '../../hooks/use-sorted-phone-countries'
import {isE164PhoneNumber, parsePhoneNumber, removeLeadingZeroes} from '../../utils/phone-utils'
import {COUNTRY_MESSAGES, PHONE_COUNTRIES} from '../../constants'
import styles from './index.recipe'

const getNumericPhoneInputValue = (value) => value.replace(/\D/g, '')
const getCompletePhoneValue = (dialCode, value) => (value ? `${dialCode}${value}` : '')
const getPhoneValueForCountryChange = (localPhoneValue, dialCode) => {
    const numericPhoneValue = getNumericPhoneInputValue(localPhoneValue)
    return getCompletePhoneValue(dialCode, removeLeadingZeroes(numericPhoneValue))
}

const messages = defineMessages({
    label: {defaultMessage: 'Phone Number', id: 'use_profile_fields.label.phone'},
    required: {
        defaultMessage: 'Please enter your phone number.',
        id: 'use_address_fields.error.please_enter_your_phone_number'
    },
    invalid: {
        defaultMessage: 'Please enter a valid phone number',
        id: 'use_address_fields.error.not_correct_format'
    }
})

const PhoneCountryFlag = ({countryCode, loading}) =>
    // Taiwan uses a transparent flag placeholder. https://dept-de.atlassian.net/browse/STOK-12283
    countryCode === 'TW' ? (
        <Box
            __css={styles.countryFlag}
            aria-hidden="true"
            width="24px"
            height="18px"
            bg="transparent"
        />
    ) : (
        <FlagImage __css={styles.countryFlag} countryCode={countryCode} loading={loading} />
    )

PhoneCountryFlag.propTypes = {
    countryCode: PropTypes.string.isRequired,
    loading: PropTypes.oneOf(['eager', 'lazy'])
}

const PhoneCountrySelectorButton = ({countryCode, dialCode, isOpen}) => {
    const {formatMessage} = useIntl()
    const selectedCountryName = formatMessage(COUNTRY_MESSAGES[countryCode])

    return (
        <MenuButton
            as={Button}
            variant="unstyled"
            __css={styles.countrySelector}
            aria-label={`${selectedCountryName} ${dialCode}`}
        >
            <Box __css={styles.countrySelectorContent}>
                <PhoneCountryFlag countryCode={countryCode} loading="eager" />
                {dialCode}
                <Box
                    as="span"
                    aria-hidden="true"
                    data-open={isOpen ? 'true' : undefined}
                    __css={styles.countrySelectorIndicator}
                />
            </Box>
        </MenuButton>
    )
}

PhoneCountrySelectorButton.propTypes = {
    countryCode: PropTypes.string.isRequired,
    dialCode: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired
}

const PhoneCountrySelectorMenuList = memo(({countryCode, onCountryChange}) => {
    const {formatMessage} = useIntl()
    const phoneCountries = useSortedPhoneCountries()

    return (
        <MenuList
            __css={styles.menuList}
            motionProps={styles.menuListMotion}
            rootProps={styles.menuListRoot}
        >
            {phoneCountries.map((phoneCountry) => (
                <MenuItem
                    key={phoneCountry.countryCode}
                    __css={styles.countryOption}
                    aria-current={phoneCountry.countryCode === countryCode ? 'true' : undefined}
                    onClick={() => onCountryChange(phoneCountry)}
                >
                    <PhoneCountryFlag countryCode={phoneCountry.countryCode} />
                    <Box as="span" __css={styles.countryDialCode}>
                        {phoneCountry.dialCode}
                    </Box>
                    <Box as="span" __css={styles.countryName}>
                        {formatMessage(COUNTRY_MESSAGES[phoneCountry.countryCode])}
                    </Box>
                </MenuItem>
            ))}
        </MenuList>
    )
})

PhoneCountrySelectorMenuList.propTypes = {
    countryCode: PropTypes.string.isRequired,
    onCountryChange: PropTypes.func.isRequired
}

if (process.env.NODE_ENV === 'development') {
    PhoneCountrySelectorMenuList.displayName = 'PhoneCountrySelectorMenuList'
}

const PhoneNumberInput = ({
    name = 'phone',
    control,
    error,
    label,
    helpText,
    required,
    inputProps,
    defaultCountryCode,
    defaultValue,
    rules,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const currentCountry = useCountry()
    const fallbackCountryCode = defaultCountryCode?.toUpperCase() || currentCountry
    const isRequired = required ?? (rules ? Boolean(rules.required) : true)
    const {
        field: {value: fieldValue, onChange: onPhoneChange}
    } = useController({name, control, defaultValue})
    const storedPhone = fieldValue ?? defaultValue
    const storedPhoneRef = useRef(storedPhone)
    storedPhoneRef.current = storedPhone
    const phoneParts = parsePhoneNumber(storedPhone, fallbackCountryCode)

    const [selectedCountryCode, setSelectedCountryCode] = useState(() => phoneParts.countryCode)
    const selectedCountry = useMemo(
        () =>
            PHONE_COUNTRIES.find(({countryCode}) => countryCode === selectedCountryCode) ||
            PHONE_COUNTRIES.find(({countryCode}) => countryCode === fallbackCountryCode),
        [fallbackCountryCode, selectedCountryCode]
    )

    const hasUserEditedRef = useRef(false)
    const phoneLabel = label || formatMessage(messages.label)
    const getLocalPhoneValue = useCallback(
        (value) => {
            const localPhoneValue = parsePhoneNumber(value, selectedCountry.countryCode).value
            return (
                localPhoneValue ||
                // Preserves existing unparseable values so users can correct them.
                (typeof value === 'string' && value && !isE164PhoneNumber(value) ? value : '')
            )
        },
        [selectedCountry.countryCode]
    )
    const parsePhoneInputValue = (value) => {
        hasUserEditedRef.current = true
        return getCompletePhoneValue(selectedCountry.dialCode, getNumericPhoneInputValue(value))
    }
    const phoneInputProps = ({value, onChange}) => ({
        autoComplete: 'tel',
        __css: styles.input,
        ...inputProps,
        onBlur: (event) => {
            inputProps?.onBlur?.(event)
            if (hasUserEditedRef.current) {
                onChange(
                    getCompletePhoneValue(
                        selectedCountry.dialCode,
                        removeLeadingZeroes(getLocalPhoneValue(value))
                    )
                )
            }
        }
    })
    const handleCountryChange = useCallback(
        (phoneCountry) => {
            setSelectedCountryCode(phoneCountry.countryCode)
            onPhoneChange(
                getPhoneValueForCountryChange(
                    getLocalPhoneValue(storedPhoneRef.current),
                    phoneCountry.dialCode
                )
            )
        },
        [getLocalPhoneValue, onPhoneChange]
    )

    return (
        <Field
            {...props}
            name={name}
            label={phoneLabel}
            type="tel"
            control={control}
            error={error}
            placeholder={selectedCountry.placeholder}
            helpText={helpText}
            defaultValue={defaultValue}
            formatDisplayValue={getLocalPhoneValue}
            parseInputValue={parsePhoneInputValue}
            inputGroupProps={{__css: styles.inputGroup}}
            inputProps={phoneInputProps}
            leftElement={
                <Menu gutter={-2} flip={false} isLazy>
                    {({isOpen}) => (
                        <>
                            <PhoneCountrySelectorButton
                                countryCode={selectedCountry.countryCode}
                                dialCode={selectedCountry.dialCode}
                                isOpen={isOpen}
                            />
                            <PhoneCountrySelectorMenuList
                                countryCode={selectedCountry.countryCode}
                                onCountryChange={handleCountryChange}
                            />
                        </>
                    )}
                </Menu>
            }
            rules={{
                ...rules,
                required: isRequired ? rules?.required || formatMessage(messages.required) : null,
                validate: {
                    ...(typeof rules?.validate === 'function' ? {custom: rules.validate} : {}),
                    ...(typeof rules?.validate === 'object' ? rules.validate : {}),
                    e164: (value) =>
                        !value || isE164PhoneNumber(value) || formatMessage(messages.invalid)
                }
            }}
        />
    )
}

PhoneNumberInput.propTypes = {
    name: PropTypes.string,
    control: PropTypes.object.isRequired,
    error: PropTypes.object,
    label: PropTypes.string,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    inputProps: PropTypes.object,
    defaultCountryCode: PropTypes.string,
    defaultValue: PropTypes.string,
    rules: PropTypes.object
}

export default PhoneNumberInput
